import { useEffect, useRef, useState } from "react";
import { preferredBatchLabel } from "@capture/shared";
import { formatDuration } from "@/features/verification/lib/format-duration";
import type { RemittanceStationPayload } from "../api/client";
import { formatMoneyCents as money } from "../lib/money";
import styles from "./RemittanceStation.module.css";

const MODE_LABEL: Record<RemittanceStationPayload["batch"]["mode"], string> = {
  combined: "Key + Balance", key: "Key", balance: "Balance",
};

/**
 * Local elapsed-time span for the station header. The VS's SessionTimer is
 * tightly coupled to the verification pane store (`usePaneStore`), which
 * this station doesn't mount, so it isn't extractable as-is — this reuses
 * only the shared, pure `formatDuration` helper and tracks its own
 * mount-time start (Design §5 requires a session timer here).
 */
function StationSessionTimer() {
  const startTime = useRef(Date.now());
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className={styles.headerSessionTimer} data-testid="station-session-timer" title="Operator session time">
      Session: {formatDuration(Date.now() - startTime.current)}
    </span>
  );
}

export function StationHeader({
  payload, acceptedCount, totalCount, batchDeltaCents,
}: {
  payload: RemittanceStationPayload;
  acceptedCount: number;
  totalCount: number;
  batchDeltaCents: number;
}) {
  // E-050 — the shared precedence helper, not a hand-inlined copy of it.
  const label = preferredBatchLabel(payload.batch);
  return (
    <header className={styles.header}>
      <div className={styles.headerBatch}>{label}</div>
      <div className={styles.headerProgress}>{acceptedCount}/{totalCount} transactions accepted</div>
      <div className={styles.headerRollup} data-balanced={batchDeltaCents === 0}>
        Batch Δ {money(batchDeltaCents)}
      </div>
      <div className={styles.headerMode}>{MODE_LABEL[payload.batch.mode]}</div>
      <StationSessionTimer />
    </header>
  );
}
